import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalBody,
	Alert,
	Box,
	ModalFooter,
	Button,
	AlertTitle,
	AlertIcon,
	AlertDescription,
	useDisclosure,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LoanTypeViewModel } from "./loan-type.view-model";
import { ButtonDelete } from "components/elements/buttons/button-delete";
import { apiRequest } from "utils/api";
import { getErrorMessage } from "utils/errorHandler";
import { useContext, useState } from "react";
import { AlertsContext } from "providers/alerts";

interface LoanTypeDeleteButtonProps {
	loanType: LoanTypeViewModel;
	onDelete: (loanType: LoanTypeViewModel) => void;
}

export function LoanTypeDeleteButton({
	loanType,
	onDelete,
}: LoanTypeDeleteButtonProps) {
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { showSuccess, showError } = useContext(AlertsContext);

	const [loading, setLoading] = useState(false);

	async function handle(
		fn: () => Promise<LoanTypeViewModel>,
		errorTitle: string
	) {
		try {
			setLoading(true);

			const loanType = await fn();

			showSuccess({
				title: "Success",
				description: "The request was successful!",
			});

			onClose();

			return loanType;
		} catch (err) {
			showError({
				title: errorTitle,
				description: getErrorMessage(err),
			});
			throw err;
		} finally {
			setLoading(false);
		}
	}

	async function handleDeleteLoanType() {
		const result = await handle(deleteLoanType, "Delete Failed");
		onDelete(result);
	}

	async function deleteLoanType(): Promise<LoanTypeViewModel> {
		await apiRequest("/supabase/loan_types/delete", {
			loan_type_id: loanType.id,
		});

		return loanType;
	}

	return (
		<>
			<ButtonDelete onClick={onOpen} float="right">
				Delete Loan Type
			</ButtonDelete>

			<Modal isOpen={isOpen} onClose={onClose}>
				<ModalOverlay />
				<ModalContent borderRadius="16px">
					<ModalBody p={6}>
						<Alert status="error" borderRadius="8px">
							<AlertIcon />
							<Box>
								<AlertTitle>Delete LoanType {loanType.title}?</AlertTitle>
								<AlertDescription>
									Deleting a loan type will delete all loan fields and loan type
									itself.
								</AlertDescription>
							</Box>
						</Alert>
					</ModalBody>

					<ModalFooter gap={2} pt={0} pb={6}>
						<Button variant="outline" onClick={onClose}>
							Cancel
						</Button>
						<Button
							leftIcon={<FontAwesomeIcon icon={["far", "trash-alt"]} />}
							variant="red"
							onClick={handleDeleteLoanType}
							isLoading={loading}
						>
							Confirm
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
}
